/**
 * Integrytas Group — PFW Third-Party Portal Administration.
 * Page: PFW.PORTAL_ADMIN
 *
 * Manage a third party's own roles and users. A platform/company admin picks a
 * company, then maintains its company roles (single-role model, with page grants)
 * and its portal users (create, assign the one role, reset password). Every write
 * goes through the delegated SECURITY DEFINER RPCs (pfw_upsert_company_role,
 * pfw_set_company_role_pages, pfw_admin_create_portal_user, pfw_assign_portal_role,
 * pfw_set_portal_password) which re-check company-admin authority server-side — the
 * same RPCs a portal-user company admin uses, so this screen mirrors what a third
 * party can self-serve.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import {
  PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, type GridColumnDef,
} from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { FacetFilter } from '../../components/common/FacetFilter';

const PAGE_CODE = 'PFW.PORTAL_ADMIN';

// §17.B11: labelled constant for portal-user status codes (CHECK enum).
const USER_STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  ACTIVE:   { labelKey: 'pfw_admin.user_status.ACTIVE',   label: 'Active',   variant: 'success' },
  LOCKED:   { labelKey: 'pfw_admin.user_status.LOCKED',   label: 'Locked',   variant: 'danger'  },
  DISABLED: { labelKey: 'pfw_admin.user_status.DISABLED', label: 'Disabled', variant: 'neutral' },
  PENDING:  { labelKey: 'pfw_admin.user_status.PENDING',  label: 'Pending',  variant: 'warning' },
};

interface Company { pfw_company_id: number; internal_code: string; name: string }
interface Role { company_role_id: number; internal_code: string; is_admin_role: string; is_active: string; display_text?: string }
interface PortalUser { portal_user_id: string; username: string; display_name: string | null; status: string; company_role_id: number | null; role_code?: string }
interface PageOpt { internal_code: string; display_text: string }
interface AccessLogRow { access_log_id: number; view_code: string | null; dataset_code: string | null; action: string; created_at: string; username?: string }

export function PfwPortalAdminPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <Inner session={session} />
    </PersonalizationProvider>
  );
}

function Inner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  // From the session, not a server probe: csm_current_is_admin() resolves the
  // JWT, which mimic does not swap, so probing it showed the REAL admin's
  // answer while impersonating someone else. What the screen offers should
  // follow the effective user, like page_access does.
  const isCsmAdmin = session.effective_user.is_admin;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [pageOpts, setPageOpts] = useState<PageOpt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [roleModal, setRoleModal] = useState<Role | null | undefined>(undefined);
  const [userModal, setUserModal] = useState<boolean>(false);
  const [ceiling, setCeiling] = useState<Set<string>>(new Set());
  const [accessLog, setAccessLog] = useState<AccessLogRow[]>([]);
  // §17.B13: expand/contract related-children — one open-set per grid.
  const detailRoles = useExpandedRows<number>();
  const detailUsers = useExpandedRows<string>();
  // §17.B12: Active facet for the roles list, defaulting to Active-only.
  const [roleActive, setRoleActive] = useState<string[]>(['Y']);

  useEffect(() => {
    void apiDb.from('pfw_company').select('pfw_company_id, internal_code, name').eq('status', 'ACTIVE').order('name')
      .then(({ data }) => setCompanies((data ?? []) as Company[]));
    void Promise.all([
      apiDb.from('csm_page_master').select('internal_code').eq('is_active', 'Y').order('internal_code'),
      apiDb.from('cds_dataset').select('internal_code, name').eq('is_active', 'Y').order('internal_code'),
    ]).then(([{ data: pages }, { data: datasets }]) => {
      const pageOptions = (pages ?? []).map((p: { internal_code: string }) => ({ internal_code: p.internal_code, display_text: p.internal_code }));
      const datasetOptions = (datasets ?? []).map((d: { internal_code: string; name: string }) => ({ internal_code: d.internal_code, display_text: `${d.internal_code} (${t('pfw_admin.dataset_tag', 'dataset')}: ${d.name})` }));
      setPageOpts([...pageOptions, ...datasetOptions]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!companyId) { setRoles([]); setUsers([]); setCeiling(new Set()); setAccessLog([]); return; }
    setBusy(true); setError(null);
    const cid = Number(companyId);
    const [{ data: r }, { data: u }, { data: c }, { data: log }] = await Promise.all([
      apiDb.from('pfw_company_role').select('company_role_id, internal_code, is_admin_role, is_active, pfw_company_role_tl!left(display_text, locale_code)').eq('pfw_company_id', cid).order('internal_code'),
      apiDb.from('pfw_portal_user').select('portal_user_id, username, display_name, status, company_role_id').eq('pfw_company_id', cid).order('username'),
      apiDb.from('pfw_company_page').select('view_code').eq('pfw_company_id', cid),
      apiDb.from('pfw_portal_access_log').select('access_log_id, view_code, dataset_code, action, created_at, pfw_portal_user!left(username)').eq('pfw_company_id', cid).order('access_log_id', { ascending: false }).limit(50),
    ]);
    type RoleJoin = Role & { pfw_company_role_tl?: { display_text: string; locale_code: string }[] };
    const rolesFlat = ((r ?? []) as RoleJoin[]).map(x => ({
      ...x, display_text: x.pfw_company_role_tl?.find(tl => tl.locale_code === 'EN_US')?.display_text ?? x.internal_code,
    }));
    const roleCode = new Map(rolesFlat.map(x => [x.company_role_id, x.internal_code]));
    setRoles(rolesFlat);
    setUsers(((u ?? []) as PortalUser[]).map(x => ({ ...x, role_code: x.company_role_id ? roleCode.get(x.company_role_id) ?? '—' : '—' })));
    setCeiling(new Set(((c ?? []) as { view_code: string }[]).map(x => x.view_code)));
    type LogJoin = AccessLogRow & { pfw_portal_user?: { username: string } | null };
    setAccessLog(((log ?? []) as LogJoin[]).map(x => ({ ...x, username: x.pfw_portal_user?.username ?? '—' })));
    setBusy(false);
  }, [companyId]);
  useEffect(() => { void load(); }, [load]);

  const saveCeiling = async (codes: Set<string>) => {
    setError(null);
    const { data } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('pfw_set_company_ceiling', {
      p_company_id: Number(companyId), p_view_codes: [...codes],
    });
    if (!data?.ok) { setError(data?.error_code ?? 'PFW.CEILING_SAVE_FAILED'); return; }
    setCeiling(codes);
  };

  // Related-children resolved from already-loaded data (names never raw ids/codes).
  const buildRoleGroups = (roleId: number): RelatedChildGroup[] => [
    {
      key: 'users',
      table: 'csm_user_master', pkColumn: 'user_id',
      title: t('pfw_admin.related.users', 'Users'),
      items: users.filter(u => u.company_role_id === roleId).map(u => ({ id: u.portal_user_id, label: u.display_name || u.username, title: u.username })),
    },
  ];
  const hasRoleRelated = (roleId: number) => buildRoleGroups(roleId).some(g => g.items.length > 0);

  const buildUserGroups = (portalUserId: string): RelatedChildGroup[] => {
    const u = users.find(x => x.portal_user_id === portalUserId);
    const r = u && u.company_role_id != null ? roles.find(x => x.company_role_id === u.company_role_id) : undefined;
    return [
      {
        key: 'role',
        table: 'pfw_company_role', pkColumn: 'company_role_id',
        title: t('pfw_admin.related.role', 'Role'),
        items: r ? [{ id: r.company_role_id, label: r.display_text ?? r.internal_code, title: r.internal_code }] : [],
      },
    ];
  };
  const hasUserRelated = (portalUserId: string) => buildUserGroups(portalUserId).some(g => g.items.length > 0);

  const shownRoles = roles.filter(r => roleActive.length === 0 || roleActive.includes(r.is_active));

  const ROLE_COLS: GridColumnDef[] = [
    { key: 'internal_code', header: t('pfw_admin.role.code', 'Role code'), defaultWidth: 180 },
    { key: 'display_text',  header: t('pfw_admin.role.name', 'Name'), defaultWidth: 200 },
    { key: 'is_admin_role', header: t('pfw_admin.role.admin', 'Company admin'), defaultWidth: 120 },
    { key: 'is_active',     header: t('pfw_admin.role.active', 'Active'), defaultWidth: 90 },
  ];
  const USER_COLS: GridColumnDef[] = [
    { key: 'username',     header: t('pfw_admin.user.username', 'Username'), defaultWidth: 160 },
    { key: 'display_name', header: t('pfw_admin.user.name', 'Name'), defaultWidth: 180 },
    { key: 'role_code',    header: t('pfw_admin.user.role', 'Role'), defaultWidth: 160 },
    { key: 'status',       header: t('pfw_admin.user.status', 'Status'), defaultWidth: 100 },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('pfw_admin.title', 'Portal Administration')}</span>
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      {error && (
        <div className="px-3 pt-2">
          <ErrorBanner
            message={t('pfw_admin.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void load()}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}

      <div className="border-b border-csm-border bg-csm-surface-alt px-3 py-2">
        <div className="max-w-md">
          <FilteredModalSingleSelect label={t('pfw_admin.company', 'Company (third party)')}
            options={companies.map(c => ({ internal_code: String(c.pfw_company_id), display_text: `${c.name} (${c.internal_code})` }))}
            selectedCode={companyId} onChange={c => setCompanyId(c ?? '')} allowClear={false} />
        </div>
      </div>

      {!companyId ? (
        <div className="flex-1 p-8 text-center text-csm-sm text-csm-ink-soft">{t('pfw_admin.pick_company', 'Select a company to manage its roles and users.')}</div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-auto p-3">
          <section className="flex min-h-0 flex-col rounded border border-csm-border">
            <div className="flex h-8 items-center gap-2 border-b border-csm-border bg-csm-surface-alt px-2">
              <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_admin.roles', 'Roles')}</span>
              <FacetFilter label={t('pfw_admin.role.active', 'Active')} options={[{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }]} selected={roleActive} onChange={setRoleActive} />
              <button onClick={() => setRoleModal(null)} className="ml-auto h-6 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover">{t('pfw_admin.new_role', '+ Role')}</button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <PersonalizedGrid componentKey="pfw_roles" columns={ROLE_COLS} sourceTable="pfw_company_role">
                {(cols) => (
                  <table className="min-w-full text-csm-sm">
                    <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft"><tr><th className="w-7 px-2 py-1" title={t('common.related.header', 'Related records')} />{cols.map(c => <th key={c.key} className="px-2 py-1 text-left">{c.header}</th>)}<th className="px-2 py-1 text-left" style={{ width: '1%' }}>{t('common.edit', 'Edit')}</th></tr></thead>
                    <tbody>{shownRoles.map(r => (
                      <Fragment key={r.company_role_id}>
                      <tr className="border-t border-csm-border-soft hover:bg-csm-surface-alt">
                        <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detailRoles.isOpen(r.company_role_id)} hasChildren={hasRoleRelated(r.company_role_id)} onToggle={() => detailRoles.toggle(r.company_role_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                        </td>
                        {cols.map(c => (
                          <td key={c.key} className="px-2 py-1">
                            {c.key === 'is_admin_role' || c.key === 'is_active'
                              ? <YesNoBadge value={(r as unknown as Record<string, unknown>)[c.key] as 'Y' | 'N'} />
                              : String((r as unknown as Record<string, unknown>)[c.key] ?? '')}
                          </td>
                        ))}
                        <td className="px-2 py-1 text-right whitespace-nowrap">
                          <button onClick={() => setRoleModal(r)} className="inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>
                        </td>
                      </tr>
                      {detailRoles.isOpen(r.company_role_id) && (
                        <tr><td colSpan={cols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRoleGroups(r.company_role_id)} session={session} /></td></tr>
                      )}
                      </Fragment>))}
                      {!busy && shownRoles.length === 0 && (
                        <tr><td colSpan={cols.length + 2} className="py-3">
                          <EmptyState variant="no-data" title={t('pfw_admin.no_roles', 'No roles yet.')} action={<button onClick={() => setRoleModal(null)} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('pfw_admin.new_role', '+ Role')}</button>} />
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded border border-csm-border">
            <div className="flex h-8 items-center gap-2 border-b border-csm-border bg-csm-surface-alt px-2">
              <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_admin.users', 'Users')}</span>
              <button onClick={() => setUserModal(true)} className="ml-auto h-6 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover">{t('pfw_admin.new_user', '+ User')}</button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <PersonalizedGrid componentKey="pfw_users" columns={USER_COLS} sourceTable="pfw_portal_user">
                {(cols) => (
                  <table className="min-w-full text-csm-sm">
                    <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft"><tr><th className="w-7 px-2 py-1" title={t('common.related.header', 'Related records')} />{cols.map(c => <th key={c.key} className="px-2 py-1 text-left">{c.header}</th>)}</tr></thead>
                    <tbody>{users.map(u => (
                      <Fragment key={u.portal_user_id}>
                      <tr className="border-t border-csm-border-soft">
                        <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detailUsers.isOpen(u.portal_user_id)} hasChildren={hasUserRelated(u.portal_user_id)} onToggle={() => detailUsers.toggle(u.portal_user_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                        </td>
                        {cols.map(c => (
                          <td key={c.key} className="px-2 py-1">
                            {c.key === 'status'
                              ? (() => {
                                  const meta = USER_STATUS_META[u.status];
                                  return <StatusBadge label={meta ? t(meta.labelKey, meta.label) : u.status} variant={meta?.variant ?? 'neutral'} />;
                                })()
                              : String((u as unknown as Record<string, unknown>)[c.key] ?? '')}
                          </td>
                        ))}
                      </tr>
                      {detailUsers.isOpen(u.portal_user_id) && (
                        <tr><td colSpan={cols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildUserGroups(u.portal_user_id)} session={session} /></td></tr>
                      )}
                      </Fragment>))}
                      {!busy && users.length === 0 && (
                        <tr><td colSpan={cols.length + 1} className="py-3">
                          <EmptyState variant="no-data" title={t('pfw_admin.no_users', 'No portal users yet.')} action={<button onClick={() => setUserModal(true)} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('pfw_admin.new_user', '+ User')}</button>} />
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </PersonalizedGrid>
            </div>
          </section>

          {isCsmAdmin && (
            <section className="flex min-h-0 flex-col rounded border border-csm-border">
              <div className="flex h-8 items-center border-b border-csm-border bg-csm-surface-alt px-2">
                <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_admin.ceiling', 'Allocation Ceiling (platform admin)')}</span>
              </div>
              <div className="min-h-0 flex-1 overflow-auto p-2">
                <p className="mb-2 text-csm-xs text-csm-ink-soft">{t('pfw_admin.ceiling_hint', 'Only these pages may ever be granted to this company’s roles.')}</p>
                {pageOpts.map(p => (
                  <label key={p.internal_code} className="flex items-center gap-2 py-0.5 text-csm-sm">
                    <input type="checkbox" checked={ceiling.has(p.internal_code)}
                      onChange={() => { const n = new Set(ceiling); if (n.has(p.internal_code)) n.delete(p.internal_code); else n.add(p.internal_code); void saveCeiling(n); }}
                      className="h-3.5 w-3.5 accent-primary" />
                    <span className="font-mono text-csm-xs">{p.internal_code}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className={`flex min-h-0 flex-col rounded border border-csm-border ${isCsmAdmin ? '' : 'col-span-2'}`}>
            <div className="flex h-8 items-center border-b border-csm-border bg-csm-surface-alt px-2">
              <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_admin.access_log', 'Portal Access Log')}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-full text-csm-xs">
                <thead className="bg-csm-surface-alt uppercase tracking-wider text-csm-ink-soft">
                  <tr><th className="px-2 py-1 text-left">{t('pfw_admin.log.when', 'When')}</th><th className="px-2 py-1 text-left">{t('pfw_admin.log.user', 'User')}</th><th className="px-2 py-1 text-left">{t('pfw_admin.log.action', 'Action')}</th><th className="px-2 py-1 text-left">{t('pfw_admin.log.target', 'Target')}</th></tr>
                </thead>
                <tbody>
                  {accessLog.map(l => (
                    <tr key={l.access_log_id} className="border-t border-csm-border-soft">
                      <td className="px-2 py-1">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-2 py-1">{l.username}</td>
                      <td className="px-2 py-1">{l.action}</td>
                      <td className="px-2 py-1 font-mono">{l.view_code ?? l.dataset_code ?? '—'}{/* fk-display-ok: audit-log target spans two code spaces (pages, datasets) with no single shared name to resolve to */}</td>
                    </tr>
                  ))}
                  {accessLog.length === 0 && (
                    <tr><td colSpan={4} className="py-3">
                      <EmptyState variant="no-data" title={t('pfw_admin.log.empty', 'No portal activity recorded yet.')} />
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {roleModal !== undefined && (
        <RoleModal companyId={Number(companyId)} role={roleModal}
          pageOpts={ceiling.size ? pageOpts.filter(p => ceiling.has(p.internal_code)) : pageOpts}
          onClose={() => setRoleModal(undefined)} onSaved={() => { setRoleModal(undefined); void load(); }} onError={setError} />
      )}
      {userModal && (
        <UserModal companyId={Number(companyId)} roles={roles}
          onClose={() => setUserModal(false)} onSaved={() => { setUserModal(false); void load(); }} onError={setError} />
      )}
    </div>
  );
}

// ── Role modal: header + page grants ───────────────────────────────────────
function RoleModal({ companyId, role, pageOpts, onClose, onSaved, onError }: {
  companyId: number; role: Role | null; pageOpts: PageOpt[]; onClose: () => void; onSaved: () => void; onError: (m: string) => void;
}) {
  const { t } = useTranslation();
  const isNew = role === null;
  const [code, setCode] = useState(role?.internal_code ?? '');
  const [name, setName] = useState(role?.display_text ?? '');
  const [isAdmin, setIsAdmin] = useState((role?.is_admin_role ?? 'N') === 'Y');
  const [grants, setGrants] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!role) return;
    void apiDb.from('pfw_company_role_page').select('view_code, can_view').eq('company_role_id', role.company_role_id)
      .then(({ data }) => setGrants(new Set((data ?? []).filter((p: { can_view: string }) => p.can_view === 'Y').map((p: { view_code: string }) => p.view_code))));
  }, [role]);

  const toggle = (vc: string) => setGrants(s => { const n = new Set(s); if (n.has(vc)) n.delete(vc); else n.add(vc); return n; });

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (!code.trim()) throw new Error(t('pfw_admin.err.code', 'Role code is required.'));
      const { data: up } = await apiDb.rpc<{ ok: boolean; error_code?: string; company_role_id?: number }>('pfw_upsert_company_role', {
        p_company_id: companyId, p_internal_code: code.trim(), p_display_text: name.trim() || code.trim(),
        p_is_admin_role: isAdmin ? 'Y' : 'N', p_company_role_id: role?.company_role_id ?? null,
      });
      if (!up?.ok) throw new Error(up?.error_code ?? 'PFW.ROLE_SAVE_FAILED');
      const roleId = up.company_role_id;
      const pages = [...grants].map(vc => ({ view_code: vc, can_view: 'Y' }));
      const { data: sp } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('pfw_set_company_role_pages', { p_company_role_id: roleId, p_pages: pages });
      if (!sp?.ok) throw new Error(sp?.error_code ?? 'PFW.GRANT_FAILED');
      onSaved();
    } catch (e) { setErr((e as Error).message); onError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isNew ? t('pfw_admin.new_role', 'New role') : t('pfw_admin.edit_role', 'Edit role')} onClose={onClose} width="lg"
      notice={err ? <ErrorBanner message={t('pfw_admin.role_save_failed', "Couldn't save the role.")} detail={err} details_label={t('common.details', 'Details')} /> : undefined}
      footer={<div className="flex justify-end gap-2"><button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm">{t('common.cancel', 'Cancel')}</button><button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button></div>}>
      <div className="space-y-3 p-1">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('pfw_admin.role.code', 'Role code')}</span><input value={code} onChange={e => setCode(e.target.value)} disabled={!isNew} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt" /></label>
          <label className="text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('pfw_admin.role.name', 'Name')}</span><input value={name} onChange={e => setName(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></label>
        </div>
        <label className="flex items-center gap-2 text-csm-sm"><input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} className="h-4 w-4 accent-primary" />{t('pfw_admin.role.admin_hint', 'Company administrator (can manage this company’s users and roles)')}</label>
        <div>
          <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('pfw_admin.role.pages', 'Pages this role can view')}</div>
          <div className="max-h-64 overflow-auto rounded border border-csm-border-soft p-2">
            {pageOpts.map(p => (
              <label key={p.internal_code} className="flex items-center gap-2 py-0.5 text-csm-sm">
                <input type="checkbox" checked={grants.has(p.internal_code)} onChange={() => toggle(p.internal_code)} className="h-3.5 w-3.5 accent-primary" />
                <span className="font-mono text-csm-xs">{p.internal_code}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── User modal: create + assign the single role ────────────────────────────
function UserModal({ companyId, roles, onClose, onSaved, onError }: {
  companyId: number; roles: Role[]; onClose: () => void; onSaved: () => void; onError: (m: string) => void;
}) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (!username.trim()) throw new Error(t('pfw_admin.err.username', 'Username is required.'));
      if (password.length < 8) throw new Error(t('pfw_admin.err.password', 'Password must be at least 8 characters.'));
      const { data } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('pfw_admin_create_portal_user', {
        p_company_id: companyId, p_username: username.trim(), p_password: password,
        p_display_name: displayName.trim() || null, p_email: null,
        p_company_role_id: roleId ? Number(roleId) : null,
      });
      if (!data?.ok) throw new Error(data?.error_code ?? 'PFW.USER_CREATE_FAILED');
      onSaved();
    } catch (e) { setErr((e as Error).message); onError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={t('pfw_admin.new_user', 'New portal user')} onClose={onClose} width="md"
      notice={err ? <ErrorBanner message={t('pfw_admin.user_create_failed', "Couldn't create the portal user.")} detail={err} details_label={t('common.details', 'Details')} /> : undefined}
      footer={<div className="flex justify-end gap-2"><button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm">{t('common.cancel', 'Cancel')}</button><button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('pfw_admin.create', 'Create')}</button></div>}>
      <div className="space-y-3 p-1">
        <label className="block text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('pfw_admin.user.username', 'Username')}</span><input value={username} onChange={e => setUsername(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></label>
        <label className="block text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('pfw_admin.user.name', 'Display name')}</span><input value={displayName} onChange={e => setDisplayName(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></label>
        <label className="block text-csm-sm"><span className="mb-1 block text-csm-ink-soft">{t('pfw_admin.user.password', 'Initial password')}</span><input type="password" value={password} onChange={e => setPassword(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></label>
        <div><FilteredModalSingleSelect label={t('pfw_admin.user.role', 'Role')} options={roles.map(r => ({ internal_code: String(r.company_role_id), display_text: r.display_text ?? r.internal_code }))} selectedCode={roleId} onChange={c => setRoleId(c ?? '')} allowClear /></div>
      </div>
    </Modal>
  );
}
